import { roundedRect } from "./roundedRect.js";

export interface BannerPalette {
  readonly bannerBg: string;
  readonly bannerBorder: string;
  readonly bannerTextColor: string;
}

export interface DotColors {
  readonly base: string;
  readonly highlight: string;
  readonly shadow: string;
  readonly border: string;
}

export interface BannerView {
  readonly text: string;
  readonly bg: string;
  readonly dot: DotColors | null;
}

export interface BannerLayout {
  readonly canvasWidth: number;
  readonly y: number;
  readonly height: number;
  readonly radius: number;
  readonly font: string;
  readonly dotRadius: number;
  readonly contentPadding: number;
  readonly rotation: 0 | 180;
}

export function drawBanner(
  ctx: CanvasRenderingContext2D,
  view: BannerView,
  palette: BannerPalette,
  layout: BannerLayout,
): void {
  ctx.save();
  ctx.font = layout.font;
  const textWidth = ctx.measureText(view.text).width;
  const dotSpace = view.dot ? layout.dotRadius * 2 + 10 : 0;
  const bannerWidth = textWidth + dotSpace + layout.contentPadding * 2;
  const bannerX = (layout.canvasWidth - bannerWidth) / 2;
  const centerY = layout.y + layout.height / 2;

  if (layout.rotation === 180) {
    const centerX = bannerX + bannerWidth / 2;
    ctx.translate(centerX, centerY);
    ctx.rotate(Math.PI);
    ctx.translate(-centerX, -centerY);
  }

  ctx.shadowColor = "rgba(0, 0, 0, 0.3)";
  ctx.shadowBlur = 8;
  ctx.shadowOffsetY = 2;
  roundedRect(ctx, bannerX, layout.y, bannerWidth, layout.height, layout.radius);
  ctx.fillStyle = view.bg;
  ctx.fill();
  ctx.shadowColor = "transparent";
  ctx.shadowBlur = 0;
  ctx.shadowOffsetY = 0;

  roundedRect(ctx, bannerX, layout.y, bannerWidth, layout.height, layout.radius);
  ctx.strokeStyle = palette.bannerBorder;
  ctx.lineWidth = 1;
  ctx.stroke();

  const contentStartX = bannerX + layout.contentPadding;
  if (view.dot) {
    const dotX = contentStartX + layout.dotRadius;
    drawDot(ctx, dotX, centerY, layout.dotRadius, view.dot);
  }

  const textX = contentStartX + dotSpace + (bannerWidth - dotSpace - layout.contentPadding * 2) / 2;
  ctx.font = layout.font;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillStyle = palette.bannerTextColor;
  ctx.fillText(view.text, textX, centerY);

  ctx.restore();
}

function drawDot(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  r: number,
  colors: DotColors,
): void {
  const grad = ctx.createRadialGradient(
    x - r * 0.3,
    y - r * 0.3,
    r * 0.1,
    x,
    y,
    r,
  );
  grad.addColorStop(0, colors.highlight);
  grad.addColorStop(0.6, colors.base);
  grad.addColorStop(1, colors.shadow);

  ctx.beginPath();
  ctx.arc(x, y, r, 0, Math.PI * 2);
  ctx.fillStyle = grad;
  ctx.fill();
  ctx.strokeStyle = colors.border;
  ctx.lineWidth = 1;
  ctx.stroke();
}
